<!-- CarePlan_AddModule_Modal.vue -->
<template>
  <!-- Modal mode: fixed overlay -->
  <div v-if="!inline" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="fixed inset-0 bg-gray-800/60" @click="$emit('close')"></div>

      <div class="bg-white rounded-xl shadow-xl border border-gray-200 max-w-4xl w-full relative z-10 max-h-[90vh] overflow-hidden flex flex-col">
        <div class="px-6 py-4 border-b border-gray-200 bg-primary-100">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-semibold text-gray-900">Add New Module</h3>
            <button @click="$emit('close')" class="text-gray-600 hover:text-gray-900">
              <FontAwesomeIcon :icon="['fas', 'xmark']" />
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto px-6 py-5">
          <AddModuleForm
            :selectedThreadTypeId="selectedThreadTypeId"
            :selectedThreadType="selectedThreadType"
            :moduleTemplates="moduleTemplates"
            :selectedTemplate="selectedTemplate"
            :formData="formData"
            :allowedStatuses="allowedStatuses"
            :sortedTemplateFields="sortedTemplateFields"
            @update:selectedThreadTypeId="selectedThreadTypeId = $event"
            @domain-change="onDomainChange"
            @select-template="selectTemplate"
            @add-goal="addGoal"
            @remove-goal="removeGoal"
            @add-intervention="addIntervention"
            @remove-intervention="removeIntervention"
          />
        </div>

        <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            @click="$emit('close')"
            type="button"
            class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            @click="save"
            type="button"
            :disabled="!isValid"
            class="btn-primary"
          >
            Save Module
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Inline mode: renders directly in parent -->
  <div v-else class="flex flex-col">
    <div class="px-6 py-4 border-b flex items-center justify-between">
      <h3 class="text-lg font-semibold">Add New Module</h3>
      <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
        <FontAwesomeIcon :icon="['fas', 'times']" />
      </button>
    </div>

    <div class="px-6 py-4">
      <AddModuleForm
        :selectedThreadTypeId="selectedThreadTypeId"
        :selectedThreadType="selectedThreadType"
        :moduleTemplates="moduleTemplates"
        :selectedTemplate="selectedTemplate"
        :formData="formData"
        :allowedStatuses="allowedStatuses"
        :sortedTemplateFields="sortedTemplateFields"
        @update:selectedThreadTypeId="selectedThreadTypeId = $event"
        @domain-change="onDomainChange"
        @select-template="selectTemplate"
        @add-goal="addGoal"
        @remove-goal="removeGoal"
        @add-intervention="addIntervention"
        @remove-intervention="removeIntervention"
      />
    </div>

    <div class="px-6 py-4 border-t flex justify-end space-x-3">
      <button
        @click="$emit('close')"
        type="button"
        class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        Cancel
      </button>
      <button
        @click="save"
        type="button"
        :disabled="!isValid"
        class="btn-primary"
      >
        Save Module
      </button>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SurveyFieldRenderer from '@/components/widgetcomponents/JSON_Editors/SurveyFieldRenderer.vue';

// Inline form body component to avoid duplicating template
const AddModuleForm = {
  components: { FontAwesomeIcon, SurveyFieldRenderer },
  props: [
    'selectedThreadTypeId', 'selectedThreadType', 'moduleTemplates',
    'selectedTemplate', 'formData', 'allowedStatuses', 'sortedTemplateFields'
  ],
  emits: [
    'update:selectedThreadTypeId', 'domain-change', 'select-template',
    'add-goal', 'remove-goal', 'add-intervention', 'remove-intervention'
  ],
  methods: {
    formatStatus(status) {
      return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
    }
  },
  template: `
    <div class="space-y-6">
      <!-- Step 1: Domain Selection -->
      <div>
        <label class="block text-sm font-medium mb-1">Care Domain</label>
        <select
          :value="selectedThreadTypeId"
          @change="$emit('update:selectedThreadTypeId', Number($event.target.value) || null); $emit('domain-change')"
          class="w-full border rounded px-3 py-2"
        >
          <option value="">Select a domain...</option>
          <option v-for="threadType in $parent.threadTypes" :key="threadType.id" :value="threadType.id">
            {{ threadType.name }}
          </option>
        </select>
      </div>

      <!-- Step 2: Template Selection (if domain selected) -->
      <div v-if="selectedThreadType && moduleTemplates.length > 0">
        <label class="block text-sm font-medium mb-1">Module Template</label>
        <div class="space-y-2">
          <div
            @click="$emit('select-template', null)"
            class="p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
            :class="{ 'border-primary-500 bg-primary-50': !selectedTemplate }"
          >
            <div class="flex items-center">
              <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-2 text-gray-400" />
              <div>
                <p class="font-medium">Custom Module</p>
                <p class="text-sm text-gray-500">Start from scratch without template fields</p>
              </div>
            </div>
          </div>

          <div
            v-for="template in moduleTemplates"
            :key="template.template_id"
            @click="$emit('select-template', template)"
            class="p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
            :class="{ 'border-primary-500 bg-primary-50': selectedTemplate?.template_id === template.template_id }"
          >
            <div>
              <p class="font-medium">{{ template.name }}</p>
              <p class="text-sm text-gray-500">{{ template.description }}</p>
              <div class="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                <span v-if="template.typical_duration_weeks">
                  <FontAwesomeIcon :icon="['fas', 'clock']" class="mr-1" />
                  {{ template.typical_duration_weeks }} weeks
                </span>
                <span v-if="template.fields">
                  {{ template.fields.length }} fields
                </span>
                <span v-if="template.default_goals">
                  {{ template.default_goals.length }} default goals
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Module Details -->
      <div v-if="selectedThreadType" class="space-y-4 pt-4 border-t">
        <div>
          <label class="block text-sm font-medium mb-1">
            Module Title <span class="text-red-500">*</span>
          </label>
          <input
            v-model="formData.module_data.title"
            type="text"
            placeholder="Enter a specific title for this module"
            class="w-full border rounded px-3 py-2"
          >
        </div>

        <div>
          <label class="block text-sm font-medium mb-1">Initial Status</label>
          <select v-model="formData.module_data.status" class="w-full border rounded px-3 py-2">
            <option
              v-for="status in allowedStatuses"
              :key="status"
              :value="status"
            >
              {{ formatStatus(status) }}
            </option>
          </select>
        </div>

        <div v-if="selectedTemplate && selectedTemplate.fields && selectedTemplate.fields.length > 0" class="space-y-4">
          <h4 class="text-sm font-semibold text-gray-700">Module Information</h4>
          <div v-for="field in sortedTemplateFields" :key="field.id">
            <SurveyFieldRenderer
              :field="field"
              v-model="formData.module_data.field_values[field.id]"
            />
          </div>
        </div>

        <!-- Goals -->
        <div>
          <div class="flex justify-between items-center mb-2">
            <label class="text-sm font-medium">Goals</label>
            <button @click="$emit('add-goal')" type="button" class="text-sm text-primary-600 hover:text-primary-700">
              <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1" />
              Add Goal
            </button>
          </div>
          <div class="space-y-2">
            <div
              v-for="(goal, i) in formData.module_data.goals"
              :key="goal.id || i"
              class="bg-gray-50 p-3 rounded"
            >
              <input v-model="goal.title" placeholder="Enter goal description" class="w-full border rounded px-2 py-1 mb-2">
              <div class="flex items-center justify-between">
                <div v-if="goal.measurement_type" class="text-xs text-gray-500">
                  Type: {{ goal.measurement_type }}
                  <span v-if="goal.target_value"> &bull; Target: {{ goal.target_value }}</span>
                </div>
                <button @click="$emit('remove-goal', i)" type="button" class="text-sm text-red-600 hover:text-red-700">Remove</button>
              </div>
            </div>
            <div v-if="formData.module_data.goals.length === 0" class="text-sm text-gray-500">No goals added yet</div>
          </div>
        </div>

        <!-- Interventions -->
        <div>
          <div class="flex justify-between items-center mb-2">
            <label class="text-sm font-medium">Interventions</label>
            <button @click="$emit('add-intervention')" type="button" class="text-sm text-primary-600 hover:text-primary-700">
              <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1" />
              Add Intervention
            </button>
          </div>
          <div class="space-y-2">
            <div
              v-for="(intervention, i) in formData.module_data.interventions"
              :key="intervention.id || i"
              class="bg-gray-50 p-3 rounded"
            >
              <input v-model="intervention.title" placeholder="Enter intervention" class="w-full border rounded px-2 py-1 mb-2">
              <div class="flex items-center justify-between">
                <div v-if="intervention.frequency" class="text-xs text-gray-500">Frequency: {{ intervention.frequency }}</div>
                <button @click="$emit('remove-intervention', i)" type="button" class="text-sm text-red-600 hover:text-red-700">Remove</button>
              </div>
            </div>
            <div v-if="formData.module_data.interventions.length === 0" class="text-sm text-gray-500">No interventions added yet</div>
          </div>
        </div>
      </div>
    </div>
  `
};

export default {
  components: {
    FontAwesomeIcon,
    SurveyFieldRenderer,
    AddModuleForm
  },

  props: {
    carePlanId: {
      type: Number,
      required: true
    },
    threadTypes: {
      type: Array,
      default: () => []
    },
    inline: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedThreadTypeId: null,
      selectedTemplate: null,

      formData: {
        thread_type_id: null,
        module_data: {
          title: '',
          status: 'planned',
          module_type: 'custom',
          template_used: null,
          field_values: {},
          field_schema: null,
          goals: [],
          interventions: [],
          barriers: []
        }
      },

      fieldErrors: {}
    };
  },

  computed: {
    selectedThreadType() {
      if (!this.selectedThreadTypeId) return null;
      return this.threadTypes.find(tt => tt.id === this.selectedThreadTypeId);
    },

    moduleTemplates() {
      return this.selectedThreadType?.metadata?.module_templates || [];
    },

    domainConfig() {
      return this.selectedThreadType?.metadata?.domain_config || {};
    },

    allowedStatuses() {
      return this.domainConfig.allowed_statuses || ['planned', 'active', 'on_hold', 'completed'];
    },

    sortedTemplateFields() {
      if (!this.selectedTemplate?.fields) return [];
      return [...this.selectedTemplate.fields].sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : 999;
        const orderB = b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });
    },

    isValid() {
      if (!this.selectedThreadTypeId || !this.formData.module_data.title?.trim()) {
        return false;
      }

      if (this.selectedTemplate?.fields) {
        const requiredFields = this.selectedTemplate.fields.filter(f => f.required);
        for (const field of requiredFields) {
          const value = this.formData.module_data.field_values[field.id];
          if (!this.hasValue(value)) {
            return false;
          }
        }
      }

      return true;
    }
  },

  methods: {
    onDomainChange() {
      this.selectedTemplate = null;
      this.formData.thread_type_id = this.selectedThreadTypeId;
      this.formData.module_data = {
        title: '',
        status: this.allowedStatuses[0] || 'planned',
        module_type: 'custom',
        template_used: null,
        field_values: {},
        field_schema: null,
        goals: [],
        interventions: [],
        barriers: []
      };
      this.fieldErrors = {};
    },

    selectTemplate(template) {
      this.selectedTemplate = template;

      if (template) {
        this.formData.module_data.template_used = template.template_id;
        this.formData.module_data.module_type = template.template_id;

        if (template.fields) {
          this.formData.module_data.field_schema = {
            title: template.name,
            fields: template.fields
          };
        }

        if (template.fields) {
          template.fields.forEach(field => {
            if (field.type === 'checkbox') {
              this.formData.module_data.field_values[field.id] = [];
            } else if (field.type === 'boolean') {
              this.formData.module_data.field_values[field.id] = null;
            } else {
              this.formData.module_data.field_values[field.id] = '';
            }
          });
        }

        this.formData.module_data.goals = (template.default_goals || []).map(goal => ({
          ...goal,
          id: Date.now() + Math.random(),
          status: 'in_progress'
        }));

        this.formData.module_data.interventions = (template.default_interventions || []).map(intervention => ({
          ...intervention,
          id: Date.now() + Math.random(),
          status: 'planned'
        }));

        if (template.typical_duration_weeks) {
          this.formData.module_data.typical_duration_weeks = template.typical_duration_weeks;
        }
      } else {
        this.formData.module_data.template_used = null;
        this.formData.module_data.module_type = 'custom';
        this.formData.module_data.field_values = {};
        this.formData.module_data.field_schema = null;
        this.formData.module_data.goals = [];
        this.formData.module_data.interventions = [];
      }
    },

    hasValue(value) {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      return true;
    },

    formatStatus(status) {
      return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
    },

    addGoal() {
      this.formData.module_data.goals.push({
        id: Date.now(),
        title: '',
        status: 'in_progress'
      });
    },

    removeGoal(index) {
      this.formData.module_data.goals.splice(index, 1);
    },

    addIntervention() {
      this.formData.module_data.interventions.push({
        id: Date.now(),
        title: '',
        status: 'planned'
      });
    },

    removeIntervention(index) {
      this.formData.module_data.interventions.splice(index, 1);
    },

    async save() {
      if (!this.isValid) {
        this.$emit('show-message', 'Please fill in all required fields', 'error');
        return;
      }

      try {
        const payload = {
          thread_type_id: this.formData.thread_type_id,
          module_data: {
            ...this.formData.module_data,
            care_plan_id: this.carePlanId
          }
        };

        const response = await this.$axios.post(
          `api/careplans/${this.carePlanId}/add-module/`,
          payload
        );

        if (response.data.success) {
          this.$emit('added');
          this.$emit('show-message', 'Module added successfully', 'success');
        } else {
          throw new Error(response.data.error || 'Failed to add module');
        }
      } catch (error) {
        console.error('Error saving module:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Failed to save module';
        this.$emit('show-message', errorMessage, 'error');
      }
    }
  }
};
</script>
